"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowRight, Mail, Home } from "lucide-react";
import { useState, useEffect } from "react";

// Updated ButtonData and SectionData with type definitions
interface ButtonData {
  text: string;
  url: string;
  variant?: "primary" | "secondary" | "outline";
  icon?: React.ReactNode;
}

interface SectionData {
  button?: ButtonData[];
  [key: string]: any;
}

interface CustomErrorPageProps {
  data?: {
    subtitle?: string;
    title?: string;
    description?: string;
    sections?: SectionData[];
    primaryCta?: ButtonData;
    secondaryCta?: ButtonData;
  };
}

// Updated product categories based on existing product types
const PRODUCT_CATEGORIES = [
  "Haldi Mehndi",
  "Sea Shell Jewellery",
  "Flower Jewellery",
  "Kaleere",
  "Earrings",
  "Bridal Jewellery",
];

export default function CustomErrorPage({ data }: CustomErrorPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([]);

  const heading = data?.subtitle || "404 ERROR";
  const title = data?.title || "Page Not Found";
  const description =
    data?.description ||
    "Sorry, the page you are looking for doesn't exist or has been moved.";

  // Handling buttons and default CTA logic
  const buttons: ButtonData[] =
    data?.sections
      ?.filter((section): section is SectionData =>
        Array.isArray(section?.button)
      )
      ?.flatMap((section) => section.button)
      ?.filter((btn: any): btn is ButtonData => btn?.text && btn?.url) || [];

  const primaryButton: ButtonData = buttons[0] ||
    data?.primaryCta || {
      text: "Return Home",
      url: "/",
      variant: "primary",
      icon: <Home className="w-4 h-4" />,
    };

  const secondaryButton: ButtonData = buttons[1] ||
    data?.secondaryCta || {
      text: "Contact Support",
      url: "/contact",
      variant: "outline",
      icon: <Mail className="w-4 h-4" />,
    };

  // Button class logic with theme-specific classes
  const getButtonClasses = (button: ButtonData) => {
    const base =
      "relative overflow-hidden group rounded-xl px-8 py-4 text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5";

    switch (button.variant) {
      case "primary":
        return `${base} bg-gradient-to-r from-[#C368AF] to-[#42BD00] text-white hover:from-[#42BD00] hover:to-[#C368AF]`;
      case "secondary":
        return `${base} bg-gradient-to-r from-[#2d8a7f] to-[#3caa9f] text-white hover:from-[#3caa9f] hover:to-[#2d8a7f]`;
      case "outline":
        return `${base} bg-white/90 backdrop-blur-sm text-gray-800 border-2 border-[#C368AF]/30 hover:border-[#C368AF] hover:bg-white`;
      default:
        return `${base} bg-gradient-to-r from-[#42BD00] to-[#C368AF] text-white hover:from-[#C368AF] hover:to-[#42BD00]`;
    }
  };

  useEffect(() => {
    const shuffled = [...PRODUCT_CATEGORIES].sort(() => 0.5 - Math.random());
    setSuggestedCategories(shuffled.slice(0, 3));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF9F5] via-white to-[#FFFAF0] flex flex-col items-center justify-center px-4 md:px-8 py-12 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 left-10 w-72 h-72 rounded-full bg-gradient-to-r from-[#C368AF]/10 to-[#42BD00]/5 backdrop-blur-3xl"
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-10 w-96 h-96 rounded-full bg-gradient-to-r from-[#2d8a7f]/5 to-[#3caa9f]/10 backdrop-blur-3xl"
          animate={{
            y: [0, 20, 0],
            x: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 1 }}
        />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20 relative z-10 mt-20">
        {/* Left Column */}
        <div className="w-full lg:w-1/2 flex flex-col items-start space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-2"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#C368AF]/10 to-[#42BD00]/5 border border-[#C368AF]/20">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#C368AF] to-[#42BD00] animate-pulse" />
              <span className="text-sm font-semibold text-[#42BD00] tracking-wider uppercase">
                {heading}
              </span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight"
          >
            {title}
            <span className="block text-4xl md:text-5xl font-normal text-[#C368AF] mt-2">
              Elevate Your Style
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-gray-600 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            {description}
            <span className="block mt-4 font-medium text-gray-700">
              Perhaps you&apos;re looking for:
            </span>
          </motion.p>

          {/* Suggested Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap gap-3"
          >
            {suggestedCategories.map((category, index) => (
              <Link
                key={category}
                href={`/collection/${category
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="group"
              >
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200 hover:border-[#C368AF] hover:bg-white transition-all duration-300 shadow-sm hover:shadow-md">
                  <ShoppingBag className="w-4 h-4 text-[#C368AF]" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#42BD00]">
                    {category}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#C368AF] transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 pt-6 w-full"
          >
            <Link href={primaryButton.url} className="flex-1 min-w-[200px]">
              <button className={getButtonClasses(primaryButton)}>
                {primaryButton.icon}
                <span>{primaryButton.text}</span>
                <span className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>

            <Link href={secondaryButton.url} className="flex-1 min-w-[200px]">
              <button className={getButtonClasses(secondaryButton)}>
                {secondaryButton.icon}
                <span>{secondaryButton.text}</span>
                <span className="absolute inset-0 bg-gradient-to-r from-gray-50/50 to-white/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
          </motion.div>
        </div>

        {/* Right Column (Illustration) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="w-full lg:w-1/2 flex justify-center items-center relative"
        >
          <div className="relative w-full max-w-lg">
            {/* Main decorative circle */}
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#C368AF]/20 to-[#42BD00]/20 blur-3xl animate-pulse" />

              <svg
                viewBox="0 0 500 500"
                className="w-full h-auto drop-shadow-2xl"
              >
                <defs>
                  <linearGradient
                    id="jewel-gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#C368AF" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#42BD00" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#2d8a7f" stopOpacity="0.4" />
                  </linearGradient>
                </defs>
                <circle
                  cx="250"
                  cy="250"
                  r="220"
                  fill="url(#jewel-gradient)"
                  stroke="white"
                  strokeWidth="3"
                  opacity="0.9"
                />
                <circle cx="250" cy="250" r="15" fill="#C368AF" opacity="0.4">
                  <animate
                    attributeName="r"
                    values="15;18;15"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
